import { IEquipment } from '@frontend/equipment/types';
import { ICONS, Package } from '@frontend/icons';
import { Slot } from '@frontend/slot/types';
import { LuCheck, LuMinus, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { ProductCard } from '../../browse-products-by-categories/product-card/product-card.component';
import { KitReturnEntry, KitReturnSlotSelectionGroup, KitReturnStatus } from './use-kit-return';

type BadgeStatus = 'complete' | 'incomplete' | 'pending';

const KitProductMetadata = ({ entry }: { entry: KitReturnEntry }) => {
    if (!entry.product.sku && !entry.product.ean) return null;

    return (
        <div className='text-brand-muted flex w-full flex-col gap-1 text-sm font-medium wrap-break-word'>
            {entry.product.sku && <span>{entry.product.sku}</span>}
            {entry.product.ean && <span>{entry.product.ean}</span>}
        </div>
    );
};

const ScannedEquipmentMetadata = ({ equipment }: { equipment: IEquipment }) => {
    return (
        <div className='flex min-w-0 flex-col gap-1'>
            <span className='text-background-default min-w-0 text-sm font-medium wrap-break-word'>SN: {equipment.serial_number}</span>
        </div>
    );
};

const StatusBadge = ({ status }: { status: BadgeStatus }) => {
    const baseClass = 'absolute top-0 right-0 size-4 rounded-full p-0.5 text-background-surface';
    if (status === 'complete') {
        return <LuCheck className={`${baseClass} bg-status-success`} />;
    }
    if (status === 'incomplete') {
        return <LuMinus className={`${baseClass} bg-status-warning`} />;
    }
    return <LuX className={`${baseClass} bg-brand-muted`} />;
};

interface KitReturnStatusMessagesProps {
    status: KitReturnStatus;
    retryLoad: () => void;
}

export const KitReturnStatusMessages = ({ status, retryLoad }: KitReturnStatusMessagesProps) => (
    <>
        {status.kind === 'config-error' && (
            <div className='bg-background-surface inline-flex items-center justify-start self-stretch rounded-xl p-4'>
                <div className='text-status-danger justify-start text-base font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.kit-return-detail.config-error'
                        description='Error message shown when the distriSPOT is not configured correctly for kit returns'
                        defaultMessage='This distriSPOT is not configured for returns. Please contact support.'
                    />
                </div>
            </div>
        )}

        {status.kind === 'load-error' && (
            <div className='bg-background-surface inline-flex items-center justify-between gap-4 self-stretch rounded-xl p-4'>
                <div className='text-status-danger justify-start text-base font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.kit-return-detail.load-error'
                        description='Error message shown when there was an error loading the kit contents'
                        defaultMessage='We could not load the kit contents. Please try again.'
                    />
                </div>
                <button
                    type='button'
                    className='text-brand-default text-sm font-medium'
                    onClick={retryLoad}>
                    <FormattedMessage
                        id='kiosk.wizard.kit-return-detail.retry'
                        description='Button label for retrying to load the kit contents after a load error'
                        defaultMessage='Retry'
                    />
                </button>
            </div>
        )}

        {status.kind === 'empty' && (
            <div className='bg-background-surface inline-flex items-center justify-start self-stretch rounded-xl p-4'>
                <div className='text-brand-muted justify-start text-base font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.kit-return-detail.empty'
                        description='Message shown when the scanned kit does not contain any returnable items'
                        defaultMessage='This item does not belong to a returnable kit.'
                    />
                </div>
            </div>
        )}
    </>
);

interface SlotSelectionContentProps {
    groups: KitReturnSlotSelectionGroup[];
    entries: KitReturnEntry[];
    selectedSlotByEquipmentId: Map<string, Slot>;
}

const slotLabel = (slot: KitReturnSlotSelectionGroup['options'][number]['slot']): string => slot.display || slot.number || slot.id;

const slotContentLabel = (option: KitReturnSlotSelectionGroup['options'][number], entries: KitReturnEntry[]): string | null => {
    const productNameById = new Map(entries.map((entry) => [entry.product.id, entry.product.name]));
    const quantitiesByProductId = new Map<string, number>();
    let unknownQuantity = 0;

    for (const stock of option.stocks) {
        const quantity = stock.quantity || 1;
        if (!productNameById.has(stock.product_id)) {
            unknownQuantity += quantity;
            continue;
        }
        quantitiesByProductId.set(stock.product_id, (quantitiesByProductId.get(stock.product_id) ?? 0) + quantity);
    }

    const labels = [...quantitiesByProductId.entries()].map(([productId, quantity]) => `${quantity}x ${productNameById.get(productId)}`);
    if (unknownQuantity > 0) labels.push(`${unknownQuantity}x other item${unknownQuantity === 1 ? '' : 's'}`);

    return labels.length > 0 ? labels.join(', ') : null;
};

const selectedSlotOptions = (
    groups: KitReturnSlotSelectionGroup[],
    selectedSlotByEquipmentId: Map<string, Slot>
): KitReturnSlotSelectionGroup['options'][number][] => {
    const optionsBySlotId = new Map<string, KitReturnSlotSelectionGroup['options'][number]>();

    for (const group of groups) {
        const selectedSlot = selectedSlotByEquipmentId.get(group.equipment.id);
        if (!selectedSlot) continue;

        const selectedOption = group.options.find((option) => option.slot.id === selectedSlot.id);
        if (selectedOption && !optionsBySlotId.has(selectedOption.slot.id)) {
            optionsBySlotId.set(selectedOption.slot.id, selectedOption);
        }
    }

    return [...optionsBySlotId.values()];
};

export const SlotSelectionContent = ({ groups, entries, selectedSlotByEquipmentId }: SlotSelectionContentProps) => (
    <div className='flex flex-1 flex-col items-start justify-start gap-4 self-stretch px-5 pt-6 pb-20'>
        <div className='text-foreground-default w-full justify-start text-base font-medium wrap-break-word'>
            <FormattedMessage
                id='kiosk.wizard.kit-return-detail.drop-in-slot'
                description='SLOT drop instruction title'
                defaultMessage='Drop your equipment in this SLOT'
            />
        </div>

        {selectedSlotOptions(groups, selectedSlotByEquipmentId).map((option) => {
            const contentLabel = slotContentLabel(option, entries);
            return (
                <div
                    key={option.slot.id}
                    className='border-brand-primary bg-background-surface flex min-h-16 w-full items-center justify-between gap-4 rounded-lg border p-4 text-left'>
                    <div className='flex min-w-0 flex-col gap-1'>
                        <span className='text-foreground-default text-base font-semibold wrap-break-word'>{slotLabel(option.slot)}</span>
                        <span className='text-brand-muted text-sm font-medium wrap-break-word'>
                            {contentLabel ? (
                                <FormattedMessage
                                    id='kiosk.wizard.kit-return-detail.slot-content'
                                    description='Message displaying items in a SLOT'
                                    defaultMessage='Contains {items}'
                                    values={{ items: contentLabel }}
                                />
                            ) : option.hasContent ? (
                                <FormattedMessage
                                    id='kiosk.wizard.kit-return-detail.slot-has-content'
                                    description='Fallback message if there is no item names available'
                                    defaultMessage='Contains other items'
                                />
                            ) : (
                                <FormattedMessage
                                    id='kiosk.wizard.kit-return-detail.slot-empty'
                                    description='Empty SLOT message'
                                    defaultMessage='Empty SLOT'
                                />
                            )}
                        </span>
                    </div>
                    <LuCheck className='text-brand-primary size-5 shrink-0' />
                </div>
            );
        })}

        {groups.map(({ equipment }) => (
            <div
                key={equipment.id}
                className='flex w-full flex-col gap-3'>
                <ProductCard
                    product_id={equipment.product_id}
                    equipment={equipment}
                />
            </div>
        ))}
    </div>
);

interface ScannedKitEquipmentCardProps {
    entry: KitReturnEntry;
    equipment: IEquipment;
    scanIndex: number;
    scannedCount: number;
    onRemove: () => void;
}

export const ScannedKitEquipmentCard = ({ entry, equipment, scanIndex, scannedCount, onRemove }: ScannedKitEquipmentCardProps) => (
    <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
        <div className='flex w-full items-start justify-start gap-4'>
            <div className='outline-border-default relative flex size-12 shrink-0 items-center justify-center rounded-full outline-2'>
                <StatusBadge status={scannedCount === entry.quantity ? 'complete' : 'incomplete'} />
                <Package className='size-1/2' />
            </div>
            <div className='flex min-w-0 flex-1 flex-col items-start justify-center gap-1'>
                <div className='text-foreground-default w-full text-base font-semibold wrap-break-word'>{entry.product.name}</div>
                <div className='text-brand-muted w-full text-base font-medium wrap-break-word'>
                    <FormattedMessage
                        id='kiosk.wizard.kit-return-detail.scanned-card-count'
                        description='Counter for a scanned item within the product quantity'
                        defaultMessage='{scanned} / {required} scanned'
                        values={{ scanned: scanIndex + 1, required: entry.quantity }}
                    />
                </div>
                <KitProductMetadata entry={entry} />
            </div>
        </div>

        <div className='bg-brand-primary flex w-full items-center justify-between gap-3 rounded-lg p-3'>
            <ScannedEquipmentMetadata equipment={equipment} />
            <button
                type='button'
                aria-label={`Remove ${equipment.serial_number}`}
                className='inline-flex size-8 shrink-0 items-center justify-center'
                onClick={onRemove}>
                <ICONS.BUTTON.DELETE className='text-background-default size-5' />
            </button>
        </div>
    </div>
);

interface MissingProductCardProps {
    entry: KitReturnEntry;
    scannedCount: number;
    missingCount: number;
}

export const MissingProductCard = ({ entry, scannedCount, missingCount }: MissingProductCardProps) => (
    <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
        <div className='flex w-full items-start justify-start gap-4'>
            <div className='outline-border-default relative flex size-12 shrink-0 items-center justify-center rounded-full outline-2'>
                <StatusBadge status='pending' />
                <Package className='size-1/2' />
            </div>
            <div className='flex min-w-0 flex-1 flex-col items-start justify-center gap-1'>
                <div className='text-foreground-default w-full text-base font-semibold wrap-break-word'>{entry.product.name}</div>
                <div className='text-brand-muted w-full text-base font-medium wrap-break-word'>
                    {scannedCount === 0 ? (
                        <FormattedMessage
                            id='kiosk.wizard.kit-return-detail.scanned-empty-card-count'
                            description='Counter shown before any equipment for a product has been scanned'
                            defaultMessage='{scanned} / {required} scanned'
                            values={{ scanned: 0, required: entry.quantity }}
                        />
                    ) : (
                        <FormattedMessage
                            id='kiosk.wizard.kit-return-detail.missing-card-count'
                            description='Counter for how many items are missing within the product quantity'
                            defaultMessage='{count} / {required} missing'
                            values={{ count: missingCount, required: entry.quantity }}
                        />
                    )}
                    {scannedCount > 0 && missingCount > 1 && (
                        <>
                            {' · '}
                            <FormattedMessage
                                id='kiosk.wizard.kit-return-detail.missing-count'
                                description='Counter for how many items are missing from the total quantity to return for a product'
                                defaultMessage='{count, plural, one {# missing} other {# missing}}'
                                values={{ count: missingCount }}
                            />
                        </>
                    )}
                </div>
                <KitProductMetadata entry={entry} />
            </div>
        </div>
    </div>
);
